import React from "react";

export default function Privacy() {
  return (
    <div className="pbp-section py-12 md:py-16 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Legal</div>
      <h1 className="mt-2 text-4xl font-semibold tracking-tighter">Privacy Policy</h1>
      <p className="mt-2 text-sm text-[#86868b]">Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="mt-10 space-y-8">
        <Section n="1" title="Introduction">
          This Privacy Policy explains how PartByPart (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses,
          shares and protects personal data. We act as a &ldquo;Data Fiduciary&rdquo; under the Digital
          Personal Data Protection Act, 2023 (DPDP Act) of India and, where applicable, as a
          &ldquo;controller&rdquo; under the EU General Data Protection Regulation (GDPR) and the
          California Consumer Privacy Act (CCPA).
        </Section>

        <Section n="2" title="Information We Collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Account data</b> from Google OAuth: name, email, profile picture, Google account ID.</li>
            <li><b>Listing data</b>: photos, model number, year, part type, condition, description, price, city, country, approximate latitude/longitude.</li>
            <li><b>Chat data</b>: messages between users, timestamps. Stored encrypted at rest.</li>
            <li><b>Device & log data</b>: IP address, device type, browser, referring page, error logs.</li>
            <li><b>Location data</b>: country and city derived from your IP via a third-party geolocation service (ipapi.co) to set your default currency and region.</li>
          </ul>
          <p className="mt-2 text-sm text-[#86868b]">We do not collect Aadhaar, PAN, financial account details, biometric data, or sensitive personal data as defined under the SPDI Rules.</p>
        </Section>

        <Section n="3" title="Lawful Basis for Processing">
          We rely on (a) your <b>consent</b> (obtained at sign-up and consent banners), (b)
          <b> performance of a contract</b> (to operate the Platform), and (c) <b>legitimate
          interest</b> (fraud prevention, security, abuse reporting) as lawful bases under the DPDP
          Act and GDPR.
        </Section>

        <Section n="4" title="How We Use Your Data">
          <ul className="list-disc pl-5 space-y-1">
            <li>To provide and maintain the Platform and personalise listings shown to you;</li>
            <li>To enable chats and connect buyers and sellers;</li>
            <li>For AI-assisted moderation, NSFW detection, and detection of stolen / counterfeit listings;</li>
            <li>To prevent fraud, abuse and illegal activity;</li>
            <li>To comply with legal obligations and respond to lawful requests by public authorities.</li>
          </ul>
        </Section>

        <Section n="5" title="AI Processing">
          When you upload a product photo we send the image to our AI provider (Gemini, via Emergent
          Universal LLM key) to suggest the model number, part type and condition, and to detect
          inappropriate content. Photos are processed for inference only and are not used to train
          third-party models. Listing descriptions are similarly checked for prohibited content.
        </Section>

        <Section n="6" title="Sharing of Data">
          We share data only with:
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Service providers</b>: object storage, geolocation, AI inference, email — all bound by data-processing terms;</li>
            <li><b>Other users</b>: your name, profile photo, listings and chat content are visible to the user you are chatting with;</li>
            <li><b>Authorities</b>: when required by valid legal process (court order, subpoena under Indian law, or equivalent in your jurisdiction).</li>
          </ul>
          <p>We do not sell personal data.</p>
        </Section>

        <Section n="7" title="International Transfers">
          PartByPart is operated from India. Your data may be processed on servers located in India,
          the European Union, or the United States. Where personal data is transferred outside your
          country, we use appropriate safeguards (e.g., Standard Contractual Clauses for EU
          transfers).
        </Section>

        <Section n="8" title="Data Retention">
          <ul className="list-disc pl-5 space-y-1">
            <li>Account data: until you delete your account;</li>
            <li>Listings: while active, plus 90 days in a soft-deleted state for fraud investigation;</li>
            <li>Chat messages: 24 months from last activity, then anonymised;</li>
            <li>Logs: up to 180 days as required under the IT Rules 2021.</li>
          </ul>
        </Section>

        <Section n="9" title="Your Rights">
          <p>Subject to applicable law you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Access</b> the personal data we hold about you;</li>
            <li><b>Correct</b> inaccurate or incomplete data;</li>
            <li><b>Erase</b> your data (right to be forgotten);</li>
            <li><b>Withdraw consent</b> previously given;</li>
            <li><b>Port</b> your data to another service in a machine-readable format;</li>
            <li><b>Nominate</b> another person to exercise your rights in case of death or incapacity (DPDP Act);</li>
            <li><b>Complain</b> to the Data Protection Board of India or your local data protection authority.</li>
          </ul>
          <p>To exercise these rights, email <a className="pbp-link" href="mailto:privacy@partbypart.com">privacy@partbypart.com</a>.
          We respond within 30 days.</p>
        </Section>

        <Section n="10" title="Security">
          We use TLS in transit, AES-256 encryption at rest for chat messages, hashed session
          tokens, rate limiting, and least-privilege access controls. No system is perfectly secure;
          you remain responsible for safeguarding your Google account.
        </Section>

        <Section n="11" title="Cookies">
          We use a single first-party httpOnly cookie (<code>session_token</code>) for
          authentication. We do not use third-party advertising cookies. By using the Platform you
          consent to this cookie. You can clear it at any time from your browser.
        </Section>

        <Section n="12" title="Children">
          The Platform is not intended for users under 18. We do not knowingly collect personal data
          from minors. If we learn we have done so, we will delete it.
        </Section>

        <Section n="13" title="Grievance Officer (India)">
          In accordance with the Information Technology Act, 2000 (and Intermediary Guidelines,
          2021) and the Digital Personal Data Protection Act, 2023:
          <br />Name: PartByPart Grievance Officer
          <br />Email: <a className="pbp-link" href="mailto:grievance@partbypart.com">grievance@partbypart.com</a>
          <br />Address: PartByPart, <b>Mumbai, Maharashtra, India</b>.
          <br />Acknowledgement within 24 hours; resolution within 15 days. Disputes arising from
          this Policy shall be subject to the <b>exclusive jurisdiction of the courts at Mumbai,
          Maharashtra</b>, with arbitration (if invoked) seated at Mumbai under the Arbitration
          and Conciliation Act, 1996.
        </Section>

        <Section n="14" title="Changes to this Policy">
          We will notify you of material changes to this Policy via in-app banner or email at least
          14 days before they take effect.
        </Section>
      </div>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-xl font-semibold tracking-tight"><span className="text-[#86868b] mr-3">{n}.</span>{title}</h2>
    <div className="mt-3 text-[#1d1d1f]/85 leading-relaxed space-y-3">{children}</div>
  </section>
);
